// useGenerationSession — the generation-flow state machine extracted out
// of App.tsx (NIGHT_AUDIT §2.9). App.tsx previously owned ~250 lines of
// streaming logic alongside its UI concerns; routing that through this
// hook leaves App as a thin render + side-effect surface.
//
// What lives here:
//   • the union state (idle / streaming / done / revising / error)
//   • submit / revise / cancel / abortStream / retryLast
//   • handleStreamEvent + handleAppName (SSE event coalescing)
//   • the lastSubmission + activeStream refs the actions coordinate over
//   • the first-build celebration latch + streak tick (status === 'done')
//
// What does NOT live here:
//   • sidebar open state + edge-swipe (pure UI concerns, stay in App.tsx)
//   • the AuthWall scrim itself (App renders it; we just signal upward)
//   • per-page telemetry (session_view ping etc) — App handles those
//
// Callers wire `onAuthWall` to raise their AuthWall and `onReady` to
// react to a successful build (e.g. play a celebration).

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { GenerationMode } from '../components/PromptInput';
import { streamGeneration } from './sse';
import type { CreatorAssetWire } from './creator-assets';
import { RecoverableStream, CONNECTION_LOST_MESSAGE } from './error-recovery';
import { getDeviceId, freshUuid } from './deviceId';
import {
  saveRecentApp,
  findRecentApp,
  touchRecentApp,
  type RecentApp,
  type AppVersion
} from './recent-apps';
import type { CapabilityNote } from '@vibeshell/shared';
import { fetchServerApp } from './creator-apps-api';
import { recordBuildToday } from './streak';
import { upsertApp } from './supabase-bridge';
import {
  hasCelebratedFirstBuild,
  markFirstBuildCelebrated
} from '../components/CelebrationOverlay';
import type { StreamEvent } from '@vibeshell/shared';

/** §A4 — GenGate queue position, surfaced while a gen waits for a slot. */
export interface QueueInfo {
  position: number;
  etaSeconds: number;
  queueLength: number;
}

/**
 * An honest, render-only summary of ONE attachment the creator sent along
 * with the prompt. Derived from the exact `CreatorAssetWire` array handed to
 * `streamGeneration`, so the progress UI can acknowledge only what the
 * pipeline actually received — never a file the upload dropped or a shape the
 * request body never carried.
 *
 * `name` + `kind` are the honest name/type. There is deliberately NO byte
 * `size`: the /v1/generate wire shape carries a sheet's parsed rows and an
 * image/PDF's URL/key — never a file size (`toWire` strips `bytes`) — so we
 * surface the ONE size we truthfully received, a sheet's `rowCount`, and leave
 * it undefined for image/pdf rather than inventing a number.
 */
export interface StreamAttachment {
  name: string;
  kind: CreatorAssetWire['kind'];
  /** Parsed row count for a 'rows' sheet (present on the wire); undefined for
   *  image/pdf, whose size never rides the wire. */
  rowCount?: number;
}

/** Map the wire assets actually sent to /v1/generate into the honest,
 *  render-only acknowledgement shape. Fed the SAME array the request body
 *  carried, so the UI can only ever reflect what the pipeline received;
 *  returns undefined when nothing was attached. */
export function describeAttachments(
  assets: CreatorAssetWire[] | undefined
): StreamAttachment[] | undefined {
  if (!assets?.length) return undefined;
  return assets.map((a) => ({
    name: a.name,
    kind: a.kind,
    ...(a.kind === 'rows' && Array.isArray(a.rows) ? { rowCount: a.rows.length } : {})
  }));
}

export interface StreamingState {
  status: 'streaming';
  sessionId: string;
  prompt: string;
  mode: GenerationMode;
  startedAt: number;
  events: StreamEvent[];
  appName?: string;
  /** Present only while the GenGate holds this gen in the FIFO queue; cleared
   *  (undefined) the moment a slot is granted and real synthesis begins. */
  queueInfo?: QueueInfo;
  /** Honest, render-only echo of the attachments the creator sent with this
   *  prompt — carried so the progress UI can acknowledge them. Mirrors exactly
   *  the wire assets handed to `streamGeneration`; absent when none were sent. */
  attachments?: StreamAttachment[];
}

export interface DoneState {
  status: 'done';
  app: RecentApp;
  events: StreamEvent[];
  /** A non-destructive failure notice — set when a REVISE fails (rate
   *  limit, terminal error, exhausted reconnects). The base app is
   *  untouched and still live, so the user stays ON it and reads why the
   *  refinement didn't apply, instead of a dead-end "Generation failed /
   *  Start over" screen that hid their working app (audit finding). */
  notice?: string;
  /** §P2-Y — the honest post-build note for a best-effort / not-yet-SUPPORTED build:
   *  what it can't do yet + the closest real alternative. Set only on CREATE, only
   *  when there's something to disclose. Informational (not an error) + dismissible. */
  capabilityNote?: CapabilityNote;
}

export interface ReviseState {
  status: 'revising';
  base: RecentApp;
  refinementPrompt: string;
  sessionId: string;
  startedAt: number;
  events: StreamEvent[];
  appName?: string;
  queueInfo?: QueueInfo;
}

export interface ErrorState {
  status: 'error';
  message: string;
  retryable: boolean;
}

export type GenerationState =
  | { status: 'idle' }
  | StreamingState
  | DoneState
  | ReviseState
  | ErrorState;

export interface UseGenerationSessionOptions {
  /**
   * The gateway tripped GUEST_LIMIT_REACHED — the caller raises an AuthWall.
   * §F02 — for a fresh gen the just-submitted draft is handed up so the wall
   * can stash it: the composer was already cleared on submit, so signing in
   * and returning would otherwise drop the idea. A revise passes nothing (the
   * app stays live and the user returns to it, not to an empty composer).
   */
  onAuthWall: (pendingDraft?: { prompt: string; mode: GenerationMode }) => void;
  /** True iff the caller wants the first-build celebration latched on. */
  onFirstBuild?: () => void;
}

export interface UseGenerationSessionResult {
  state: GenerationState;
  /** Drop back to idle, tearing down any active stream. */
  reset: () => void;
  /** Kick off a fresh generation. */
  submit: (prompt: string, mode: GenerationMode, assets?: CreatorAssetWire[]) => Promise<void>;
  /** Apply a refinement to the currently-displayed app. */
  revise: (refinementPrompt: string, assets?: CreatorAssetWire[]) => Promise<void>;
  /** User-pressed Cancel — tear down + return to idle. */
  cancel: () => void;
  /** Silence-timeout auto-abort — stop the stream, stay on the surface. */
  abortStream: () => void;
  /** Re-fire the last submission verbatim. */
  retryLast: () => void;
  /** Swap the displayed version while keeping the full history. */
  restoreVersion: (v: AppVersion) => void;
  /**
   * §S680 — the ADDRESSABLE editor restore path (#apps/:id/edit). Looks the
   * app up in local recents; falls back to the server row (the durable
   * source of truth) so the editor survives a reload / fresh browser; if
   * neither knows the app, honestly lands on the Workspace list.
   */
  openApp: (appId: string) => Promise<void>;
  /** Pre-fill the prompt without leaving the home canvas. Used by sidebar starter ideas. */
  primePrompt: (prompt: string) => void;
  /** Seed value the prompt dock should pre-fill with. */
  promptSeed: string;
  /** Clear the prompt seed once the dock has consumed it. */
  consumePromptSeed: () => void;
  /** Dismiss the revise-failure notice shown on the done screen. */
  clearNotice: () => void;
}

interface StreamContext {
  mode: GenerationMode;
  prompt: string;
  sessionId: string;
  isRevise?: boolean;
}

export function useGenerationSession(
  opts: UseGenerationSessionOptions
): UseGenerationSessionResult {
  const [state, setState] = useState<GenerationState>({ status: 'idle' });
  const [promptSeed, setPromptSeed] = useState('');

  // Track the active RecoverableStream so Cancel/Retry can tear it down,
  // and the last submission so a Retry doesn't need re-typed input.
  const activeStream = useRef<RecoverableStream | null>(null);
  const lastSubmission = useRef<{ prompt: string; mode: GenerationMode } | null>(
    null
  );

  // First-build celebration latch — fires once per device, the first
  // time status transitions to 'done'. localStorage gates across-session
  // idempotency; the parent's onFirstBuild lets it actually render the
  // confetti overlay.
  useEffect(() => {
    if (state.status !== 'done') return;
    try {
      recordBuildToday();
    } catch {
      /* streak ledger is best-effort */
    }
    if (!hasCelebratedFirstBuild()) {
      // §V2 honesty — 'done' is also reached by RESTORING an existing app
      // (openApp / #apps/:id/edit), and "Your first Pomela app is live"
      // must never fire for a mere open. lastSubmission is only set by a
      // real submit in this session, so gate the celebration on it.
      if (!lastSubmission.current) return;
      markFirstBuildCelebrated();
      opts.onFirstBuild?.();
    }
  }, [state.status, opts]);

  // Open-app-from-Pomy + seed-prompt-from-Duplicate. Both lanes park their
  // handoff payload in sessionStorage; we consume it once on mount.
  useEffect(() => {
    const openId = sessionStorage.getItem('vs:creator:open-app');
    if (openId) {
      sessionStorage.removeItem('vs:creator:open-app');
      const app = findRecentApp(openId);
      if (app) {
        touchRecentApp(openId);
        setState({ status: 'done', app, events: [] });
        return;
      }
    }
    const seed = sessionStorage.getItem('vs:creator:seed-prompt');
    if (seed) {
      sessionStorage.removeItem('vs:creator:seed-prompt');
      setPromptSeed(seed);
    }
  }, []);

  const handleAppName = useCallback((name: string): void => {
    setState((current) => {
      if (current.status === 'streaming' || current.status === 'revising') {
        return { ...current, appName: name };
      }
      return current;
    });
  }, []);

  // §A4 — GenGate queue frames. Set queueInfo while waiting; ANY non-queued
  // event (synthesis_started etc.) clears it via handleStreamEvent below.
  const handleQueued = useCallback(
    (position: number, etaSeconds: number, queueLength: number): void => {
      setState((current) => {
        if (current.status === 'streaming' || current.status === 'revising') {
          return { ...current, queueInfo: { position, etaSeconds, queueLength } };
        }
        return current;
      });
    },
    []
  );

  const handleStreamEvent = useCallback(
    (event: StreamEvent, ctx: StreamContext) => {
      setState((current) => {
        if (current.status !== 'streaming' && current.status !== 'revising') {
          return current;
        }
        const events = [...current.events, event];

        if (event.type === 'deploy_complete') {
          const previousApp = current.status === 'revising' ? current.base : null;
          const now = Date.now();
          const version: AppVersion = {
            at: now,
            label: previousApp ? ctx.prompt : 'Original',
            kind: previousApp ? 'revise' : 'create',
            appId: event.appId,
            url: event.url,
            ogImageUrl: event.ogImageUrl,
            manifest: event.manifest,
            jsx: event.jsx
          };
          // §S679 draft law — a REVISE result deploys as its own immutable
          // build and becomes the family's pending DRAFT; the app's stable
          // identity (the appId/URL customers hold) does NOT change until the
          // creator publishes. The workspace previews the draft via the
          // family's draft pointer, adopted below.
          const familyId = previousApp ? previousApp.appId : event.appId;
          const familyUrl = previousApp ? previousApp.url : event.url;
          const finished: RecentApp = {
            appId: familyId,
            url: familyUrl,
            ogImageUrl: previousApp ? previousApp.ogImageUrl ?? event.ogImageUrl : event.ogImageUrl,
            prompt: previousApp ? previousApp.prompt : ctx.prompt,
            manifest: event.manifest,
            createdAt: previousApp ? previousApp.createdAt : now,
            jsx: event.jsx,
            versions: previousApp ? [...previousApp.versions, version] : [version]
          };
          saveRecentApp(finished, undefined);
          if (previousApp) {
            // Adopt the revised build as the family's draft (owner-authed).
            // Guests (401/403) fall back to the pre-draft behavior: the list
            // entry swaps to the new build so their result is never lost.
            queueMicrotask(() => {
              void fetch(`/v1/apps/${encodeURIComponent(familyId)}/draft`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ draftAppId: event.appId })
              })
                .then((r) => {
                  if (!r.ok) {
                    saveRecentApp({ ...finished, appId: event.appId, url: event.url, ogImageUrl: event.ogImageUrl }, familyId);
                    setState((cur) =>
                      cur.status === 'done' && cur.app.appId === familyId
                        ? { ...cur, app: { ...cur.app, appId: event.appId, url: event.url, ogImageUrl: event.ogImageUrl } }
                        : cur
                    );
                  }
                })
                .catch(() => {});
            });
          }
          // §8.2 — dual-write to Supabase (cloud source of truth) for signed-in
          // creators. Fire-and-forget: localStorage already has it; a no-op when
          // signed out. RLS scopes the row to the creator's account.
          void upsertApp({
            appId: finished.appId,
            name: finished.manifest.name,
            prompt: finished.prompt,
            primaryColor: finished.manifest.primaryColor ?? null,
            previewUrl: finished.url,
            ogImageUrl: finished.ogImageUrl
          });
          // Bug 2 reachability ping. If the worker 404s the fresh appId
          // surface a console warning instead of letting the iframe fail
          // silently. Fire-and-forget — failure here doesn't roll back
          // the done state, the iframe watchdog will catch it.
          void fetch(finished.url, { method: 'GET', cache: 'no-store' })
            .then((r) => {
              if (!r.ok) {
                console.warn(
                  `[vibeshell] preview unreachable (HTTP ${r.status}) for ${finished.appId} at ${finished.url}`
                );
              }
            })
            .catch(() => {
              console.warn(
                `[vibeshell] preview reachability check failed for ${finished.appId}`
              );
            });
          window.posthog?.capture('generation_total', {
            sessionId: ctx.sessionId,
            appId: finished.appId,
            isRevise: !!ctx.isRevise,
            success: true
          });
          // §P2-Y — carry the honest post-build note through to the done screen. Only a
          // CREATE emits it (a revise keeps the app's already-known identity), so `previousApp`
          // guards it; the note is absent for a fully SUPPORTED build or a non-EN prompt.
          return { status: 'done', app: finished, events, capabilityNote: previousApp ? undefined : event.capabilityNote };
        }

        if (event.type === 'error') {
          window.posthog?.capture('generation_failed', { sessionId: ctx.sessionId });
          // A failed REVISE must not tear down the user's app — the base is
          // still deployed and viewable. Return to it with a notice instead
          // of the terminal error screen (which is for fresh gens, where
          // there is nothing to go back to).
          if (current.status === 'revising') {
            return { status: 'done', app: current.base, events: [], notice: event.message };
          }
          return { status: 'error', message: event.message, retryable: event.retryable };
        }

        // §A4 — any substantive synthesis event means the GenGate granted a
        // slot; clear the queue notice so the UI flips from "in line" to the
        // real synthesis progress.
        if (current.queueInfo && event.type !== 'stream_open') {
          return { ...current, events, queueInfo: undefined };
        }

        return { ...current, events };
      });
    },
    []
  );

  const submit = useCallback(
    async (prompt: string, mode: GenerationMode, assets?: CreatorAssetWire[]): Promise<void> => {
      const sessionId = freshUuid();
      const deviceId = getDeviceId();
      const startedAt = Date.now();
      lastSubmission.current = { prompt, mode };
      setPromptSeed('');
      // `attachments` is derived from the SAME `assets` that flow into the
      // request body below (`...(assets?.length ? { assets } : {})`), so the
      // waiting UI can only ever acknowledge what the pipeline received.
      setState({
        status: 'streaming',
        sessionId,
        prompt,
        mode,
        startedAt,
        events: [],
        attachments: describeAttachments(assets)
      });
      window.posthog?.capture('prompt_submitted', {
        sessionId,
        mode,
        promptLength: prompt.length,
        promptHash: await sha256(prompt)
      });

      activeStream.current?.abort();
      const recovery = new RecoverableStream({
        sessionId,
        onEvent: (event) => handleStreamEvent(event, { mode, prompt, sessionId }),
        onFatal: (message) =>
          setState({
            status: 'error',
            message: message || CONNECTION_LOST_MESSAGE,
            retryable: true
          }),
        openStream: (h) =>
          streamGeneration(
            { prompt, deviceId, sessionId, mode, ...(assets?.length ? { assets } : {}) },
            { ...h, onAuthWall: handleAuthWall, onAppName: handleAppName, onQueued: handleQueued }
          )
      });
      activeStream.current = recovery;
      await recovery.start();
    },
    [handleStreamEvent, handleAppName, handleQueued]
    // handleAuthWall is captured from the latest opts via closure below.
    // We intentionally re-create it each call rather than memo it so the
    // hook caller's opts can change without stale callbacks.
  );

  function handleAuthWall(): void {
    activeStream.current = null;
    // stateRef is declared below but assigned every render, so it is always
    // current by the time a stream event can fire.
    const current = stateRef.current;
    // §F02 — hand the fresh gen's just-submitted draft up so the wall can stash
    // it for the sign-in round trip (the composer was cleared on submit). A
    // revise keeps its live app, so there's nothing to restore into a composer.
    opts.onAuthWall(
      current.status === 'streaming' ? { prompt: current.prompt, mode: current.mode } : undefined
    );
    // When the wall fires on a REVISE, the user's app is still live and
    // perfectly viewable — return them to it, not to the empty composer.
    // (Audit finding: tapping a "keep building" capability at the free-tier
    // cap raised the wall AND tore down the post-gen screen; dismissing the
    // wall stranded the user at the composer with their app gone from view.)
    // A fresh-gen wall still lands on idle — the composer is where they were.
    if (current.status === 'revising') {
      setState({ status: 'done', app: current.base, events: [] });
    } else {
      setState({ status: 'idle' });
    }
  }

  // Phase 10 — `revise` MUST be a stable callback identity. The old
  // implementation listed `state` in its dependency array, recreating
  // the function every time the state machine ticked. That cascade
  // could push downstream effects into a render thrash (the agent that
  // audited useGenerationSession flagged it as a potential infinite-
  // loop trigger). We capture the latest state via a ref instead.
  const stateRef = useRef(state);
  stateRef.current = state;

  const revise = useCallback(
    async (refinementPrompt: string, assets?: CreatorAssetWire[]): Promise<void> => {
      const current = stateRef.current;
      if (current.status !== 'done') return;
      const base = current.app;
      // §REVISE-HYDRATE (P2, measured live 2026-07-26 on shift-fv0v) — a missing LOCAL jsx must not
      // silently become a fresh build. The old fallback ran submit(), which (a) charged **build 20**
      // instead of **revise 10** — a 2x overcharge recorded in credit_events as kind='build' — and
      // (b) produced a BRAND-NEW app instead of restyling this one, silently changing the operation
      // the creator asked for. It fired whenever localStorage lacked the jsx: a returning session, a
      // second device, or a cleared cache. Clicking a style preset in a fresh browser hit it every time.
      // The server does NOT need previousJsx: the revise path re-hydrates the base spec from D1 via
      // revise.basedOnAppId (generation-worker index.ts:721-751), so the revise stays correct without
      // it. Only a missing appId leaves nothing to revise.
      if (!base.appId) {
        void submit(refinementPrompt, 'quick');
        return;
      }
      const previousJsx = base.jsx ?? '';
      const sessionId = freshUuid();
      const deviceId = getDeviceId();
      const startedAt = Date.now();
      setState({
        status: 'revising',
        base,
        refinementPrompt,
        sessionId,
        startedAt,
        events: []
      });
      window.posthog?.capture('revise_submitted', {
        sessionId,
        basedOnAppId: base.appId,
        promptLength: refinementPrompt.length
      });

      activeStream.current?.abort();
      const recovery = new RecoverableStream({
        sessionId,
        onEvent: (event) =>
          handleStreamEvent(event, {
            mode: 'quick',
            prompt: refinementPrompt,
            sessionId,
            isRevise: true
          }),
        onFatal: (message) =>
          // Even when every reconnect is exhausted, the BASE app is still
          // deployed — return the user to it with a notice rather than a
          // dead-end error screen (same audit finding as the event-error
          // branch: a failed refinement must never hide the working app).
          setState({
            status: 'done',
            app: base,
            events: [],
            notice: message || CONNECTION_LOST_MESSAGE
          }),
        openStream: (h) =>
          streamGeneration(
            {
              prompt: refinementPrompt,
              deviceId,
              sessionId,
              mode: 'quick',
              ...(assets?.length ? { assets } : {}),
              revise: {
                basedOnAppId: base.appId,
                previousJsx,
                previousTheme: base.manifest,
                previousPrompt: base.prompt
              }
            },
            { ...h, onAuthWall: handleAuthWall, onAppName: handleAppName, onQueued: handleQueued }
          )
      });
      activeStream.current = recovery;
      await recovery.start();
    },
    // Stable identity — `state` flows through `stateRef`, the others are
    // already stable useCallback identities.
    [submit, handleStreamEvent, handleAppName, handleQueued]
  );

  const cancel = useCallback((): void => {
    activeStream.current?.abort();
    activeStream.current = null;
    setState({ status: 'idle' });
  }, []);

  const abortStream = useCallback((): void => {
    activeStream.current?.abort();
    activeStream.current = null;
  }, []);

  const retryLast = useCallback((): void => {
    activeStream.current?.abort();
    activeStream.current = null;
    const last = lastSubmission.current;
    if (last) {
      void submit(last.prompt, last.mode);
    } else {
      setState({ status: 'idle' });
    }
  }, [submit]);

  const reset = useCallback((): void => {
    setState({ status: 'idle' });
  }, []);

  // §S680 — restore an app into the editor by id (the #apps/:id/edit route).
  // Monotonic token: if a second openApp supersedes an in-flight server
  // fetch, the stale resolution is dropped instead of clobbering the state.
  const openToken = useRef(0);
  const openApp = useCallback(async (appId: string): Promise<void> => {
    const token = ++openToken.current;
    // A route-driven open replaces whatever was streaming.
    activeStream.current?.abort();
    activeStream.current = null;
    const local = findRecentApp(appId);
    if (local) {
      touchRecentApp(appId);
      setState({ status: 'done', app: local, events: [] });
      return;
    }
    // Not in local recents (fresh browser / cleared cache) — the server row
    // is the durable source of truth. It returns the RecentApp shape minus
    // jsx and with an empty version list; backfill a single-version timeline
    // so the editor's history UI has an honest "Original" entry.
    const { app } = await fetchServerApp(appId);
    if (token !== openToken.current) return; // superseded by a newer open
    if (app) {
      const versions: AppVersion[] =
        app.versions && app.versions.length
          ? app.versions
          : [
              {
                at: app.createdAt,
                label: 'Original',
                kind: 'create',
                appId: app.appId,
                url: app.url,
                ogImageUrl: app.ogImageUrl,
                manifest: app.manifest,
                jsx: app.jsx
              }
            ];
      const restored: RecentApp = { ...app, versions };
      saveRecentApp(restored); // re-warm the local cache for the next open
      setState({ status: 'done', app: restored, events: [] });
      return;
    }
    // Unknown app (or not this creator's) — honest fallback to the list,
    // but only if the user is still ON this app's route (they may have
    // navigated away while the fetch was in flight).
    if ((location.hash || '').includes(appId)) location.hash = 'apps';
  }, []);

  const restoreVersion = useCallback((v: AppVersion): void => {
    setState((current) => {
      if (current.status !== 'done') return current;
      return {
        status: 'done',
        events: current.events,
        app: {
          ...current.app,
          appId: v.appId,
          url: v.url,
          ogImageUrl: v.ogImageUrl,
          manifest: v.manifest,
          jsx: v.jsx
        }
      };
    });
    window.posthog?.capture('version_restored', { appId: v.appId });
  }, []);

  const primePrompt = useCallback((prompt: string): void => {
    activeStream.current?.abort();
    activeStream.current = null;
    setState({ status: 'idle' });
    setPromptSeed(prompt);
  }, []);

  const consumePromptSeed = useCallback((): void => {
    setPromptSeed('');
  }, []);

  // Dismiss the revise-failure notice without touching the done state.
  const clearNotice = useCallback((): void => {
    setState((current) =>
      current.status === 'done' && current.notice ? { ...current, notice: undefined } : current
    );
  }, []);

  return {
    state,
    reset,
    submit,
    revise,
    cancel,
    abortStream,
    retryLast,
    restoreVersion,
    openApp,
    primePrompt,
    promptSeed,
    consumePromptSeed,
    clearNotice
  };
}

async function sha256(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf))
    .slice(0, 8)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
